<script setup lang="ts">
  import type { VNode, VNodeChild } from "vue";
  import { computed, h, ref, watch } from "vue";
  import { useRoute } from "vue-router";
  import type {
    MenuGroupOption,
    MenuNodeProps,
    MenuOption,
    MenuProps,
  } from "naive-ui";

  // import type { HTMLAttributes } from 'vue';
  import { SimpleScrollbar } from "@sa/materials";
  import type { RouteKey } from "@elegant-router/types";
  import { useAppStore } from "@/store/modules/app";
  import { useThemeStore } from "@/store/modules/theme";
  import { useRouteStore } from "@/store/modules/route";
  import { useRouterPush } from "@/hooks/common/router";

  defineOptions({
    name: "BaseMenu",
  });

  interface Props {
    darkTheme?: boolean;
    mode?: MenuProps["mode"];
    menus: App.Global.Menu[];
  }

  interface MenuChild {
    key: string;
    label: string;
    icon?: () => VNode;
    routeKey: RouteKey;
    routePath: string;
  }

  interface MenuItem {
    key: string;
    label: string;
    icon?: () => VNode;
    routeKey: RouteKey;
    routePath: string;
    children?: MenuChild[];
    footer?: boolean;
  }

  const props = withDefaults(defineProps<Props>(), {
    mode: "vertical",
  });

  const route = useRoute();
  const appStore = useAppStore();
  const themeStore = useThemeStore();
  const routeStore = useRouteStore();
  const { routerPushByKey } = useRouterPush();

  const naiveMenus = computed(() => {
    return (props.menus as MenuItem[])
      .filter((menu) => !menu.footer)
      .map((menu) => ({
        ...menu,
        icon: menu.icon,
        children: menu.children?.map((child: MenuChild) => ({
          ...child,
          icon: child.icon,
        })),
      }));
  });

  const naiveFooterMenus = computed(() => {
    return (props.menus as MenuItem[])
      .filter((menu) => menu.footer)
      .map((menu) => ({
        ...menu,
        icon: menu.icon,
        children: menu.children?.map((child: MenuChild) => ({
          ...child,
          icon: child.icon,
        })),
      }));
  });

  const renderMenuLabel = (
  option: MenuOption | MenuGroupOption
  ): VNodeChild => {
  // Skip rendering for group titles or dividers
  if (option.type === "group" || option.type === "divider") {
  return option.label as string;
  }
  
  const routeKey = option.key as RouteKey;
  const routeMeta = routeStore.getSelectedMenuMetaByKey(routeKey);
  
  // Get the path from the route meta
  const path = routeMeta?.path || "#";
  
  // Check if this is a parent menu with children
  const hasChildren = 'children' in option && option.children && option.children.length > 0;
  
  return h(
  "a",
  {
  href: path,
  onClick: (e: MouseEvent) => {
    // For parent menus with children, let NMenu handle the click
    // For leaf menu items, handle the click here
    if (!hasChildren && e.button === 0) {
      e.preventDefault();
      handleClickMenu(routeKey);
    } else if (hasChildren && e.button === 0) {
      // For parent menus, prevent default to avoid navigation
      e.preventDefault();
    }
  },
  },
  [h("span", {}, { default: () => option.label })]
  );
  };

  const isHorizontal = computed(() => props.mode === "horizontal");

  const siderCollapse = computed(
    () => themeStore.layout.mode === "vertical" && appStore.siderCollapse
  );

  const headerHeight = computed(() => `${themeStore.header.height}px`);

  const selectedKey = computed(() => {
    const { hideInMenu, activeMenu } = route.meta;
    const name = route.name as string;

    const routeName = (hideInMenu ? activeMenu : name) || name;

    return routeName;
  });

  const expandedKeys = ref<string[]>([]);

  function updateExpandedKeys() {
    if (isHorizontal.value || siderCollapse.value || !selectedKey.value) {
      expandedKeys.value = [];
      return;
    }
    expandedKeys.value = routeStore.getSelectedMenuKeyPath(selectedKey.value);
  }

  function handleClickMenu(key: RouteKey) {
    const { query } = routeStore.getSelectedMenuMetaByKey(key) || {};

    routerPushByKey(key, { query });
  }

  watch(
    () => route.name,
    () => {
      updateExpandedKeys();
    },
    { immediate: true }
  );

  const handleNodeProps: MenuNodeProps = (
    option: MenuOption | MenuGroupOption
  ) => {
    // Check if key exists and convert it to string to safely use includes
    if (!option?.key) return {};
    const key = String(option.key);

    // Count number of underscores to determine nesting level
    const underscoreCount = (key.match(/_/g) || []).length;
    const isChild = underscoreCount > 0;

    if (appStore.direction === "rtl" && isChild) {
      // Base padding is 18px, multiply by nesting level
      const paddingRight = 8 * underscoreCount;
      return {
        style: `padding-right: ${paddingRight}px; padding-left: initial;`,
      };
    }

    return {};
  };
</script>

<template>
  <SimpleScrollbar :collapsed="siderCollapse" :direction="appStore.direction">
    <div class="menu-scrollbar-container-wrapper">
      <NMenu
        v-model:expanded-keys="expandedKeys"
        :style="{ direction: appStore.direction }"
        :mode="mode"
        :value="selectedKey"
        :collapsed="siderCollapse"
        :collapsed-width="themeStore.sider.collapsedWidth"
        :collapsed-icon-size="22"
        :options="naiveMenus"
        :inverted="darkTheme"
        :root-indent="18"
        :indent="20"
        responsive
        :node-props="handleNodeProps"
        :render-label="renderMenuLabel"
        @update:value="handleClickMenu"
      />
      <NMenu
        v-model:expanded-keys="expandedKeys"
        :style="{ direction: appStore.direction }"
        :mode="mode"
        :value="selectedKey"
        :collapsed="siderCollapse"
        :collapsed-width="themeStore.sider.collapsedWidth"
        :collapsed-icon-size="22"
        :options="naiveFooterMenus"
        :inverted="darkTheme"
        :root-indent="18"
        :indent="20"
        responsive
        :node-props="handleNodeProps"
        :render-label="renderMenuLabel"
        @update:value="handleClickMenu"
      />
    </div>
  </SimpleScrollbar>
</template>

<style scoped>
  :deep(.n-menu--horizontal) {
    --n-item-height: v-bind(headerHeight) !important;
  }

  /* Fix submenu overflow */
  :deep(.n-menu) {
    /* flex: 1; */
    overflow: visible !important;
    padding-bottom: unset;
    /* Important to show submenu */
  }

  :deep(.n-submenu) {
    position: relative;
    /* Ensure submenu positioning context */
  }

  :deep(.n-submenu-children) {
    max-height: 60vh;
    /* Limit submenu height */
    /* overflow-y: auto; */
    /* Enable scrolling for submenu */
    position: relative;
    /* Keep submenu in flow */
  }

  /* If you want to keep the footer menu at bottom */
  :deep(.n-menu:last-child) {
    margin-top: auto;
  }
</style>
